/**
 * BottomNav -- persistent 4-item bottom navigation bar.
 * Tabs: Home | Yawmi | Hisni | More.
 * More opens a bottom-sheet with secondary surfaces.
 */

import type { AweniContext } from '../context/AweniContext';
import { getModule } from '../modules/registry';
import { prefersReducedMotion } from '../utils/motion';

interface NavModule {
  id: string;
  icon: string;
  label: Record<string, string>;
}

const PRIMARY_MODULE_IDS = ['home', 'yawmi', 'memoriz'] as const;
const MORE_MODULE_IDS = ['family', 'games', 'selecto', 'blog', 'balancetab', 'chat', 'community', 'profile', 'settings'] as const;

const HISNI_LABEL: Record<string, string> = {
  en: 'Hisni', fr: 'Hisni', ar: 'حصني', es: 'Hisni', de: 'Hisni',
};

const BALANCETAB_LABEL: Record<string, string> = {
  en: 'BalanceTab', fr: 'BalanceTab', ar: 'BalanceTab', es: 'BalanceTab', de: 'BalanceTab',
};

const MORE_LABEL: Record<string, string> = {
  en: 'More', fr: 'Plus', ar: 'المزيد', es: 'Más', de: 'Mehr',
};

const FILLED_ICONS: Record<string, string> = {
  home: '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><rect x="9" y="12" width="6" height="10" fill="var(--bg-primary)"/></svg>',
  yawmi: '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="12" r="10"/><polyline points="8 12 10.5 14.5 16 9" stroke="var(--bg-primary)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>',
  memoriz: '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>',
  more: '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>',
};

const MORE_ICON_OUTLINE = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>';

let _activeId = 'home';
const _buttons: Map<string, HTMLButtonElement> = new Map();
let _modules: NavModule[] = [];
let _onNavigate: ((moduleId: string) => void) | null = null;
let _moreMenuEl: HTMLElement | null = null;
let _backdropEl: HTMLElement | null = null;
let _moreMenuOpen = false;
let _langHandler: ((e: Event) => void) | null = null;
let _pillEl: HTMLDivElement | null = null;

function _applyTapBounce(btn: HTMLElement): void {
  if (prefersReducedMotion()) return;
  btn.style.transition = 'transform 80ms var(--motion-ease-standard)';
  btn.style.transform = 'scale(0.88)';
  setTimeout(() => {
    btn.style.transform = '';
    btn.style.transition = '';
  }, 80);
}

function _updatePillPosition(btn: HTMLButtonElement): void {
  if (!_pillEl) return;
  const btnWidth = btn.offsetWidth;
  const btnLeft = btn.offsetLeft;
  if (btnWidth === 0) {
    requestAnimationFrame(() => _updatePillPosition(btn));
    return;
  }
  const pillWidth = Math.min(72, btnWidth - 16);
  _pillEl.style.width = `${pillWidth}px`;
  _pillEl.style.left = `${btnLeft + (btnWidth - pillWidth) / 2}px`;
  _pillEl.style.opacity = '1';
}

export function mountBottomNav(
  container: HTMLElement,
  ctx: AweniContext,
  modules: NavModule[],
  onNavigate: (moduleId: string) => void,
): void {
  _onNavigate = onNavigate;
  _buttons.clear();
  _destroyMoreMenu();

  container.innerHTML = '';
  container.setAttribute('role', 'tablist');
  container.setAttribute('aria-label', 'Main navigation');
  container.style.cssText = `
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 56px;
    z-index: 100;
    background: var(--bottomnav-bg);
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    box-shadow: var(--ln-shadow-sm);
  `;

  // Create sliding pill in the background
  const pill = document.createElement('div');
  pill.className = 'aweni-nav-pill';
  pill.style.cssText = `
    position: absolute;
    height: 32px;
    border-radius: 16px;
    background: color-mix(in srgb, var(--ln-accent, #9C8348) 12%, transparent);
    z-index: 0;
    pointer-events: none;
    top: 12px;
    left: 0;
    width: 0px;
    opacity: 0;
    transition:
      left var(--motion-duration-300) var(--motion-ease-emphasized),
      width var(--motion-duration-200) var(--motion-ease-standard),
      opacity var(--motion-duration-200) var(--motion-ease-standard);
  `;
  container.appendChild(pill);
  _pillEl = pill;

  const locale = (ctx.i18n.locale || 'en') as string;
  const byId = new Map(modules.map((m) => [m.id, m] as const));
  _modules = PRIMARY_MODULE_IDS
    .map((id) => byId.get(id) ?? _fromRegistry(id))
    .filter((m): m is NavModule => !!m);

  for (const mod of _modules) {
    const btn = _createTabButton(mod, locale);
    btn.addEventListener('click', () => {
      if (navigator.vibrate) navigator.vibrate(10);
      _applyTapBounce(btn);
      _closeMoreMenu();
      setActiveTab(mod.id);
      onNavigate(_normalizeModuleId(mod.id));
    });
    _buttons.set(mod.id, btn);
    container.appendChild(btn);
  }

  const moreBtn = document.createElement('button');
  const moreLabelText = MORE_LABEL[locale] || MORE_LABEL.en;
  moreBtn.setAttribute('role', 'tab');
  moreBtn.setAttribute('data-module', 'more');
  moreBtn.setAttribute('aria-label', moreLabelText);
  moreBtn.setAttribute('aria-selected', 'false');
  moreBtn.setAttribute('aria-haspopup', 'true');
  moreBtn.setAttribute('aria-expanded', 'false');
  moreBtn.style.cssText = _tabButtonStyle();

  const moreIconWrap = document.createElement('span');
  moreIconWrap.className = 'tab-icon';
  moreIconWrap.style.cssText = 'display:flex;align-items:center;justify-content:center;height:24px;width:24px;';
  moreIconWrap.innerHTML = MORE_ICON_OUTLINE;

  const moreLabelEl = document.createElement('span');
  moreLabelEl.className = 'tab-label';
  moreLabelEl.style.cssText = 'font-size:10px;font-weight:500;line-height:1;white-space:nowrap;';
  moreLabelEl.textContent = moreLabelText;

  moreBtn.append(moreIconWrap, moreLabelEl);
  moreBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (navigator.vibrate) navigator.vibrate(10);
    _applyTapBounce(moreBtn);
    _toggleMoreMenu();
  });
  _buttons.set('more', moreBtn);
  container.appendChild(moreBtn);

  _buildMoreMenu(locale);
  setActiveTab(_activeId);

  // Re-label tab buttons when the app language changes
  if (_langHandler) window.removeEventListener('aweni:lang-change', _langHandler);
  _langHandler = (e: Event) => {
    const newLocale = (e as CustomEvent<{ lang: string }>).detail.lang;
    for (const [id, btn] of _buttons.entries()) {
      const labelEl = btn.querySelector('.tab-label') as HTMLElement | null;
      if (!labelEl) continue;
      if (id === 'more') {
        const lbl = MORE_LABEL[newLocale] || MORE_LABEL.en;
        labelEl.textContent = lbl;
        btn.setAttribute('aria-label', lbl);
      } else {
        const mod = _modules.find((m) => m.id === id);
        if (mod) {
          const lbl = _labelForModule(mod, newLocale);
          labelEl.textContent = lbl;
          btn.setAttribute('aria-label', lbl);
        }
      }
    }
  };
  window.addEventListener('aweni:lang-change', _langHandler);
}

function _createTabButton(mod: NavModule, locale: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.setAttribute('role', 'tab');
  btn.setAttribute('data-module', mod.id);
  const localLabel = _labelForModule(mod, locale);
  btn.setAttribute('aria-label', localLabel);
  btn.setAttribute('aria-selected', 'false');
  btn.style.cssText = _tabButtonStyle();

  const iconWrap = document.createElement('span');
  iconWrap.className = 'tab-icon';
  iconWrap.style.cssText = 'display:flex;align-items:center;justify-content:center;height:24px;width:24px;';
  const iconSvg = mod.icon.replace('<svg', '<svg width="22" height="22"');
  iconWrap.innerHTML = iconSvg;

  const label = document.createElement('span');
  label.className = 'tab-label';
  label.style.cssText = 'font-size:10px;font-weight:500;line-height:1;white-space:nowrap;';
  label.textContent = localLabel;

  btn.append(iconWrap, label);
  return btn;
}

function _buildMoreMenu(locale: string): void {
  const backdrop = document.createElement('div');
  backdrop.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.3);
    z-index: 99;
    display: none;
    opacity: 0;
    transition: opacity var(--motion-duration-base) var(--motion-easing-default);
  `;
  backdrop.addEventListener('click', () => _closeMoreMenu());
  document.body.appendChild(backdrop);
  _backdropEl = backdrop;

  const menu = document.createElement('div');
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', 'More options');
  menu.style.cssText = `
    position: fixed;
    bottom: calc(56px + env(safe-area-inset-bottom, 0px));
    left: 8px;
    right: 8px;
    z-index: 101;
    background: var(--card-bg, var(--ln-bg-card));
    border: 1px solid var(--border);
    border-radius: 16px 16px 0 0;
    box-shadow: var(--ln-shadow-lg);
    padding: 16px 12px 12px;
    display: none;
    transform: translateY(100%);
    transition: transform var(--motion-duration-base) var(--motion-easing-default), opacity var(--motion-duration-base) var(--motion-easing-default);
    opacity: 0;
    max-height: calc(72dvh - 56px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `;

  const handle = document.createElement('div');
  handle.style.cssText = `width:36px;height:4px;border-radius:4px;background:var(--border);margin:0 auto 12px;position:sticky;top:0;`;
  menu.appendChild(handle);

  for (const id of MORE_MODULE_IDS) {
    const mod = _fromRegistry(id);
    if (!mod) continue;
    const localLabel = _labelForModule(mod, locale);
    const item = document.createElement('button');
    item.setAttribute('role', 'menuitem');
    item.style.cssText = `
      display: flex;
      align-items: center;
      gap: var(--ln-space-3, 12px);
      width: 100%;
      padding: var(--ln-space-3, 12px);
      border: none;
      border-radius: var(--ln-radius-md, 12px);
      background: transparent;
      color: var(--text-primary);
      font-size: 15px;
      font-weight: 500;
      cursor: pointer;
      text-align: start;
      transition: background var(--motion-duration-fast) var(--motion-easing-default);
    `;
    item.addEventListener('mouseenter', () => { item.style.background = 'var(--hover-bg, color-mix(in srgb, var(--ln-accent) 5%, transparent))'; });
    item.addEventListener('mouseleave', () => { item.style.background = 'transparent'; });

    const iconEl = document.createElement('span');
    iconEl.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: var(--bg-secondary);
      color: var(--accent);
      flex-shrink: 0;
    `;
    const iconSvg = mod.icon.replace('<svg', '<svg width="20" height="20"');
    iconEl.innerHTML = iconSvg;

    const labelEl = document.createElement('span');
    labelEl.textContent = localLabel;

    item.append(iconEl, labelEl);
    item.addEventListener('click', () => {
      _closeMoreMenu();
      if (_onNavigate) {
        setActiveTab(mod.id);
        _onNavigate(mod.id);
      }
    });

    menu.appendChild(item);
  }

  document.body.appendChild(menu);
  _moreMenuEl = menu;
}

function _tabButtonStyle(): string {
  return `
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    height: 100%;
    background: transparent;
    border: none;
    padding: 4px 0;
    min-width: 0;
    position: relative;
    color: var(--text-secondary);
    cursor: pointer;
  `;
}

function _labelForModule(mod: NavModule, locale: string): string {
  if (mod.id === 'memoriz') return HISNI_LABEL[locale] || HISNI_LABEL.en;
  if (mod.id === 'balancetab') return BALANCETAB_LABEL[locale] || BALANCETAB_LABEL.en;
  return mod.label[locale] || mod.label.en || mod.id;
}

function _fromRegistry(id: string): NavModule | undefined {
  const mod = getModule(id);
  if (!mod) return undefined;
  return {
    id: mod.id,
    icon: mod.icon,
    label: mod.label,
  };
}

export function toggleMoreMenu(): void { _toggleMoreMenu(); }

function _toggleMoreMenu(): void {
  if (_moreMenuOpen) {
    _closeMoreMenu();
  } else {
    _openMoreMenu();
  }
}

function _openMoreMenu(): void {
  if (!_moreMenuEl || !_backdropEl) return;
  _moreMenuOpen = true;

  _backdropEl.style.display = 'block';
  _moreMenuEl.style.display = 'block';

  requestAnimationFrame(() => {
    if (_backdropEl) _backdropEl.style.opacity = '1';
    if (_moreMenuEl) {
      _moreMenuEl.style.transform = 'translateY(0)';
      _moreMenuEl.style.opacity = '1';
    }
  });

  const moreBtn = _buttons.get('more');
  if (moreBtn) moreBtn.setAttribute('aria-expanded', 'true');
}

function _closeMoreMenu(): void {
  if (!_moreMenuEl || !_backdropEl) return;
  _moreMenuOpen = false;

  _backdropEl.style.opacity = '0';
  _moreMenuEl.style.transform = 'translateY(100%)';
  _moreMenuEl.style.opacity = '0';

  setTimeout(() => {
    if (_backdropEl) _backdropEl.style.display = 'none';
    if (_moreMenuEl) _moreMenuEl.style.display = 'none';
  }, 250);

  const moreBtn = _buttons.get('more');
  if (moreBtn) moreBtn.setAttribute('aria-expanded', 'false');
}

function _destroyMoreMenu(): void {
  _moreMenuOpen = false;
  if (_moreMenuEl?.parentElement) _moreMenuEl.parentElement.removeChild(_moreMenuEl);
  if (_backdropEl?.parentElement) _backdropEl.parentElement.removeChild(_backdropEl);
  _moreMenuEl = null;
  _backdropEl = null;
}

export function setActiveTab(moduleId: string): void {
  _activeId = moduleId;
  const normalized = _normalizeTabId(moduleId);
  const isMoreModule = (MORE_MODULE_IDS as readonly string[]).includes(normalized);
  const highlightId = isMoreModule ? 'more' : normalized;

  for (const mod of _modules) {
    const btn = _buttons.get(mod.id);
    if (!btn) continue;
    const isActive = mod.id === highlightId;

    btn.setAttribute('aria-selected', String(isActive));

    const iconWrap = btn.querySelector('.tab-icon') as HTMLSpanElement;
    const label = btn.querySelector('.tab-label') as HTMLSpanElement;

    if (iconWrap) {
      if (isActive && FILLED_ICONS[mod.id]) {
        iconWrap.innerHTML = FILLED_ICONS[mod.id];
      } else {
        const iconSvg = mod.icon.replace('<svg', '<svg width="22" height="22"');
        iconWrap.innerHTML = iconSvg;
      }

      if (prefersReducedMotion()) {
        iconWrap.style.transform = isActive ? 'scale(1.15)' : 'scale(1.0)';
        iconWrap.style.transition = 'none';
      } else {
        iconWrap.style.transition = 'transform var(--motion-duration-150) var(--motion-ease-standard)';
        iconWrap.style.transform = isActive ? 'scale(1.15)' : 'scale(1.0)';
      }
    }

    if (prefersReducedMotion()) {
      btn.style.transition = 'none';
    } else {
      btn.style.transition = 'color var(--motion-duration-150) var(--motion-ease-standard)';
    }
    btn.style.color = isActive ? 'var(--ln-accent)' : 'var(--ln-text-secondary)';
    if (label) label.style.fontWeight = isActive ? '600' : '500';
  }

  const moreBtn = _buttons.get('more');
  if (moreBtn) {
    const isActive = highlightId === 'more';
    moreBtn.setAttribute('aria-selected', String(isActive));
    const moreIcon = moreBtn.querySelector('.tab-icon') as HTMLSpanElement;
    const moreLabel = moreBtn.querySelector('.tab-label') as HTMLSpanElement;
    if (moreIcon) {
      moreIcon.innerHTML = isActive ? FILLED_ICONS.more : MORE_ICON_OUTLINE;
      
      if (prefersReducedMotion()) {
        moreIcon.style.transform = isActive ? 'scale(1.15)' : 'scale(1.0)';
        moreIcon.style.transition = 'none';
      } else {
        moreIcon.style.transition = 'transform var(--motion-duration-150) var(--motion-ease-standard)';
        moreIcon.style.transform = isActive ? 'scale(1.15)' : 'scale(1.0)';
      }
    }
    
    if (prefersReducedMotion()) {
      moreBtn.style.transition = 'none';
    } else {
      moreBtn.style.transition = 'color var(--motion-duration-150) var(--motion-ease-standard)';
    }
    moreBtn.style.color = isActive ? 'var(--ln-accent)' : 'var(--ln-text-secondary)';
    if (moreLabel) moreLabel.style.fontWeight = isActive ? '600' : '500';
  }

  // Update floating background pill position
  const activeBtn = _buttons.get(highlightId);
  if (activeBtn) {
    _updatePillPosition(activeBtn);
  } else if (_pillEl) {
    _pillEl.style.opacity = '0';
  }
}

function _normalizeModuleId(moduleId: string): string {
  return moduleId === 'memoriz' ? 'hisni' : moduleId;
}

function _normalizeTabId(moduleId: string): string {
  return moduleId === 'hisni' || moduleId === 'quran' ? 'memoriz' : moduleId;
}
